import { useCallback } from 'react';
import type { Metro } from '../../types/metro';
import { useRankings, type RankingSortKey } from '../../hooks/useRankings';
import { useFilterStore } from '../../stores/filterStore';
import { SortableHeader } from './SortableHeader';
import { RankingsRow } from './RankingsRow';
import { EmptyState } from '../shared/EmptyState';

interface RankingsTableViewProps {
  readonly metros: ReadonlyArray<Metro>;
  /**
   * Optional per-row click handler. When passed, each row becomes
   * keyboard-activatable and fires this callback with the metro --
   * the page-level RowPeekDrawer wiring uses this to open the
   * peek panel. Omit for static (non-peekable) renders.
   */
  readonly onRowSelect?: (metro: Metro) => void;
}

/**
 * Q2.A columns (RANK-fix-4) — match prototype `_archive/rankings.html`
 * lines 422-434. The Severity column sorts by `ratio` underneath
 * (the pill is the visualized ratio so sorting by it = sorting by
 * the underlying number).
 *
 * CR-07 (2026-07-08, Maria 4a / client G2): every column — including
 * the rank index and the severity pill — is left-aligned, header and
 * content alike. The client's complaint was specifically the
 * header-vs-content mismatch created by mixing right/center-aligned
 * numeric columns with left-aligned text columns; uniform left
 * alignment removes the mismatch. `tabular-nums`/`font-mono` stay on
 * the numeric cells so digits still line up within a column.
 */
type ColumnSpec = {
  key: RankingSortKey | null;
  label: string;
  widthClass?: string;
};

/**
 * Column set used when no occupational group is selected — relabeled
 * in the 2026-05-18 feedback pass. Sort keys + cell renderers stay
 * the same as before; only the visible labels changed.
 */
const DEFAULT_COLUMNS: ReadonlyArray<ColumnSpec> = [
  { key: null,                label: '#',                                              widthClass: 'w-12' },
  { key: 'name',              label: 'Metro area' },
  // 2026-07-02 (V2 feedback pass): column labels expanded per the
  // Georgetown "recurring column name copy" spec.
  //   Springboard occupational groups → Springboard occupational group (singular)
  //   Share with shortages           → Share of occupational groups with shortages
  { key: 'springboardCount',  label: 'Springboard occupational group',                  widthClass: 'w-44' },
  { key: 'shortageShare',     label: 'Share of occupational groups with shortages',     widthClass: 'w-56' },
  { key: 'ratio',             label: 'Credentials-to-jobs ratio',                        widthClass: 'w-40' },
  { key: null,                label: 'Occupational group with largest shortage',        widthClass: 'w-[260px]' },
];

/**
 * Column set used when an occupational group IS selected — the table
 * pivots to per-group numbers for the lensed sector. Per
 * 2026-05-18 feedback the four data columns become: number of
 * credentials, number of jobs, credentials-to-jobs ratio, credential
 * shortage. Sort keys: `lensCredentials` / `lensJobs` / `ratio` /
 * `lensCredentials` (shortage sorts by the credentials column).
 */
const LENS_COLUMNS: ReadonlyArray<ColumnSpec> = [
  { key: null,               label: '#',                            widthClass: 'w-12' },
  { key: 'name',             label: 'Metro area' },
  // 2026-07-02 (V2 feedback pass): lens columns take the full
  // "Annual ..." wording per the recurring column-name spec.
  { key: 'lensCredentials',  label: 'Annual credentials awarded',   widthClass: 'w-44' },
  { key: 'lensJobs',         label: 'Annual job openings',          widthClass: 'w-40' },
  { key: 'ratio',            label: 'Credentials-to-jobs ratio',    widthClass: 'w-40' },
  { key: 'lensCredentials',  label: 'Annual credential shortage',   widthClass: 'w-44' },
];

/**
 * Sortable rankings table.
 *
 * CR-07 (2026-07-08): every header (including the Severity/ratio
 * column, previously centered per RANK-fix-7) now renders left-
 * aligned via SortableHeader's `'left'` default — no per-column
 * `align` override is passed anymore. See the `ColumnSpec` comment
 * above for the client rationale.
 *
 * RANKINGS-MARIA-fix-1 (2026-05-07): when an Occupational Group is
 * selected, render an explanatory banner above the table so the
 * user can see (a) that their selection took effect and (b) which
 * group's data the table is currently lensed through. Includes a
 * "Clear" affordance to drop back to the metro-wide view.
 */
export function RankingsTableView({ metros, onRowSelect }: RankingsTableViewProps) {
  const reset = useFilterStore((s) => s.reset);
  const setOccupationalGroup = useFilterStore((s) => s.setOccupationalGroup);
  const { rows, sortKey, sortDir, toggleSort, lensSectorName } = useRankings(metros);

  // Code-review apply (Warning 1): hoist the inline arrow + template
  // literal out of the JSX so they aren't re-allocated every render
  // of the parent. Section 5 ("no inline objects/arrays in JSX")
  // applies — useCallback gives a stable reference, and the
  // aria-label is computed once per lens flip instead of every paint.
  const clearLens = useCallback(
    () => setOccupationalGroup(null),
    [setOccupationalGroup],
  );
  const clearLensAriaLabel = lensSectorName
    ? `Clear ${lensSectorName} lens and return to metro-wide view`
    : '';

  return (
    <div className="flex flex-col gap-4">
      {lensSectorName && (
        <div
          // Banner uses the ice background that the filter bar above
          // wears, so the lens cue feels like it extends the filter
          // chrome rather than competing with the table chrome.
          // role="status" so screen readers announce when a lens is
          // applied / cleared (one announcement per change, not per
          // render — the React tree only mounts/unmounts this banner
          // when lensSectorName flips between null and a value).
          role="status"
          aria-live="polite"
          className="flex items-center justify-between gap-3 rounded-md border border-blue/20 bg-ice px-4 py-2.5 text-sm text-text-1"
        >
          {/* Code-review apply (Warning 2): copy narrowed from
              "sector-specific ratios" to "Severity column only" so the
              user isn't misled into expecting the Springboard Occs /
              Share w/ Shortages / Most Underproduced columns to also
              swap (lens is intentionally additive — only Severity
              changes per RANKINGS-MARIA-fix-1 scope).
              2026-07-02 (V2 feedback): reviewers wanted the lens banner
              to spell out what the lensed columns actually contain,
              rather than the terse "credentials, jobs, ratio, and
              shortage are all sector specific." Rewrote per the spec. */}
          <span>
            <span className="font-semibold text-navy">Lens:</span> Showing the
            credentials, number of job openings, credentials-to-jobs ratio, and
            credential shortages within the{' '}
            <span className="font-semibold">{lensSectorName}</span> occupational
            group for each metro.
          </span>
          <button
            type="button"
            onClick={clearLens}
            className="shrink-0 text-xs font-semibold text-blue-link underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-blue/40 rounded px-1"
            aria-label={clearLensAriaLabel}
          >
            Clear lens
          </button>
        </div>
      )}

      <div className="flex items-center justify-between text-[0.8rem] font-medium text-text-3">
        <span>Showing {rows.length} {rows.length === 1 ? 'metro' : 'metros'}</span>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="No metros match"
          description="Try clearing the search or a status filter."
          action={
            <button
              type="button"
              onClick={reset}
              className="text-xs font-semibold text-blue-link hover:underline"
            >
              Clear filters
            </button>
          }
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-white">
          {/* CR-27 (Maria 2026-07-08 #4b): the table is wider than the
              container on narrow/laptop screens, so it must scroll
              horizontally rather than clip. The prior `md:overflow-visible`
              disabled the scroll at >=768px, causing the last column
              ("Occupational group with largest shortage") to get cropped
              with no way to reach it. Always `overflow-x-auto` — matches
              the Providers table pattern the client asked us to mirror. */}
          <div className="overflow-x-auto">
            <table className="rankings-table w-full" aria-label="Metro area rankings">
            <thead>
              <tr>
                {(lensSectorName ? LENS_COLUMNS : DEFAULT_COLUMNS).map((col) => (
                  col.key ? (
                    <SortableHeader
                      key={col.label}
                      label={col.label}
                      sortDir={sortKey === col.key ? sortDir : null}
                      onSort={() => toggleSort(col.key as RankingSortKey)}
                      widthClass={col.widthClass}
                    />
                  ) : (
                    // CR-07: no `align` field left on ColumnSpec -- every
                    // non-sortable header (rank #, "Most underproduced")
                    // renders left-aligned, matching its body cells.
                    <th
                      key={col.label}
                      scope="col"
                      className={`h-9 select-none border-b-2 border-navy bg-white px-3 text-left text-[13px] font-semibold text-text-3 ${col.widthClass ?? ''}`}
                    >
                      {col.label}
                    </th>
                  )
                ))}
              </tr>
            </thead>
            <tbody aria-label="Metro rows">
              {rows.map((row) => (
                <RankingsRow
                  mode={lensSectorName ? 'lens' : 'default'}
                  key={row.metro.id}
                  rank={row.rank}
                  metro={row.metro}
                  metric={row.metric}
                  onSelect={onRowSelect}
                />
              ))}
            </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
